import { LANDING_DESCRIPTION, LANDING_TITLE } from '@/utils/messages';
import { default as SuccessIcon } from '@mui/icons-material/CheckCircleRounded';
import { Alert, Divider } from '@mui/material';
import { initiateSso, PasswordlessLogin, SsoPortal } from '@verifime/cognito-passwordless-auth';
import {
  Landing,
  LINK_REPLACEMENT_WARNING,
  RESEND_NOW_MESSAGE,
  SignInWithXeroButton,
} from '@verifime/components';
import { useFeatureToggle } from '@verifime/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';

// These paths are served by other Next apps behind rewrites, so reaching them takes a full page
// load; router.push would look for a Zone A page and 404.
// TODO: extract to external app prefix config
const OTHER_ZONE_PATH_PREFIXES = ['/risk-assessment', '/integrations'];

// The Monitoring zone sends its own location back as `from`, path plus search, so `/monitoring`
// may be followed by `/`, `?` or `#`.
function isMonitoringZonePath(path: string) {
  return /^\/monitoring(?:[/?#]|$)/.test(path);
}

function isServedByAnotherZone(path: string) {
  return (
    OTHER_ZONE_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`)) ||
    isMonitoringZonePath(path)
  );
}

export default function LoginPage() {
  const router = useRouter();
  const { isInertMagicLinkEnabled } = useFeatureToggle();
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState<string>();

  const signInRedirect = () => {
    const from = router.query['from'] as string;
    if (from) {
      let decoded = '/dashboard';
      try {
        decoded = decodeURIComponent(from);
      } catch (err) {
        console.error('Malformed "from" query parameter:', from, err);
      }
      if (isServedByAnotherZone(decoded)) {
        window.location.href = decoded;
        return;
      }
      router.push(decoded);
      return;
    }
    router.push('/dashboard');
  };

  const handleXeroSso = async () => {
    setSsoLoading(true);
    setSsoError(undefined);

    try {
      let returnPath = '/';
      if (typeof router.query.from === 'string') {
        try {
          returnPath = decodeURIComponent(router.query.from);
        } catch (err) {
          console.error('Malformed returnPath in query param "from":', router.query.from, err);
          returnPath = '/';
        }
      }

      const redirectUrl = await initiateSso({
        provider: 'xero',
        origin: window.location.origin,
        returnPath,
        portalEnv: (process.env.NEXT_PUBLIC_AUTH_PORTAL_ID as SsoPortal) || undefined,
      });

      window.location.href = redirectUrl;
    } catch (err) {
      console.error('Failed to initiate SSO:', err);
      setSsoError('Failed to start Xero sign in. Please try again.');
      setSsoLoading(false);
    }
  };

  const ssoSection = (
    <>
      <Divider sx={(theme) => ({ width: '100%', my: theme.semantic.spacing.sm })}>OR</Divider>
      <SignInWithXeroButton loading={ssoLoading} onClick={handleXeroSso} />
      {ssoError && (
        <Alert severity="error" sx={{ width: '100%' }}>
          {ssoError}
        </Alert>
      )}
    </>
  );

  return (
    <Landing
      title={LANDING_TITLE}
      description={LANDING_DESCRIPTION}
      mobileNav={isInertMagicLinkEnabled}
    >
      <PasswordlessLogin
        signInCallback={signInRedirect}
        extraSlot={ssoSection}
        magicLinkPending={
          isInertMagicLinkEnabled
            ? {
                title: 'Continue to VerifiMe',
                actionLabel: 'Continue',
                // attribution is explicitly set to undefined (never an
                // omitted key), matching customer-portal's index.tsx: a
                // client-portal user is a returning professional signing
                // into their own workspace, not an invited customer, so
                // there is no inviting organisation and no tracking
                // reference to ever resolve one from. The explicit undefined records that as a
                // deliberate "no organisation" decision, not an oversight.
                attribution: undefined,
                // requirements is explicitly set to undefined, in the same deliberate style as
                // attribution above: client-portal has no tracking reference on this page (see
                // attribution's comment), so there is no identityDocumentCount to resolve and
                // nothing to build a requirements panel from.
                requirements: undefined,
                // The label above the countdown. Approved copy (slot 06, approved 2026-09-21) - the
                // time itself is never authored here: it is built downstream from the hook's own
                // pendingSignInLinkExpiresAt, decoded from the magic link's own JWT fragment
                // (packages/cognito-passwordless-auth/src/magic-link.ts's
                // checkCurrentLocationForSignInLink, confirmed to be the link's own `exp` claim,
                // not a session's). PasswordlessLogin (cognito-passwordless-auth) already omits the
                // whole clock whenever that value is unavailable, so supplying the label here is
                // safe regardless of whether a given link's fragment decodes.
                expiryLabel: 'Your link is valid until',
                errorMessage: "We couldn't sign you in. Try the button again.",
              }
            : undefined
        }
        // Task 5 ("Link sent, one screen", settled 2026-09-17): the sign-in route's resend
        // affordance is now LinkSentSignInResend, composed inside cognito-passwordless-auth's own
        // PasswordlessLogin container (not this page) once a send has actually happened - this
        // page's own job is just the copy bag that container forwards straight into it, the same
        // board slots every route's resend composition draws from.
        signInLinkRequested={
          isInertMagicLinkEnabled
            ? {
                leadLine: "Didn't get it?",
                resendActionLabel: 'Resend the email',
                pressedInsideWindowMessage: 'Please try again later, we will let you know.',
                replacementWarning: LINK_REPLACEMENT_WARNING,
                resendNowMessage: RESEND_NOW_MESSAGE,
                codePrompt: 'Enter the code from your authenticator app.',
                resendButtonLabel: 'Resend',
                codeRejectedMessage: 'Code not accepted.',
              }
            : undefined
        }
      >
        <SuccessIcon color="success" />
      </PasswordlessLogin>
    </Landing>
  );
}
